import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { forkJoin, of, Subject } from "rxjs";
import { catchError, filter, map, switchMap, takeUntil } from "rxjs/operators";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import { DashboardService } from "../../../services/dashboard.service";

type AnyObj = { [key: string]: any };

interface ProjectReportResponse {
  version?: string;
  name?: string;
  districts?: AnyObj[];
  districts_count?: number;
  villages_count?: number;
  overview?: AnyObj;
  percentage?: AnyObj;
  officers_list?: AnyObj;
  PN_details?: AnyObj;
  new_progress_details?: AnyObj;
}

interface ChartResponse {
  start_date?: string;
  date_of_3a_expected?: string;
}

interface ReportLoadFailure {
  source: "report" | "chart";
  message: string;
  hasBackendMessage: boolean;
}

interface KpiItem {
  icon: string;
  color: "blue" | "green" | "red" | "purple";
  label: string;
  value: string;
}

interface ProgressRow {
  icon: string;
  stage: string;
  description: string;
  expected: string;
  achieved: string;
  percentage: string;
  percentValue: number;
  status: string;
}

interface SummarySection {
  title: string;
  className: string;
  columns: string[];
  rows: string[][];
}

interface VillageMetric {
  label: string;
  value: string;
  documents: VillageDocumentLink[];
}

interface VillageDocumentLink {
  url: string;
  fileName: string;
  label: string;
}

interface DistrictVillagePage {
  villages: AnyObj[];
  pageNumber: number;
  startIndex: number;
}

interface DistrictReportSection {
  districtId: string;
  districtName: string;
  districtDataIds: string;
  villagesCount: number;
  completed: string;
  onTime: string;
  delayed: string;
  totalArea: string;
  acquiredArea: string;
  remainingArea: string;
  progress: string;
  overviewPageNumber: number;
  summarySections: SummarySection[];
  villagePages: DistrictVillagePage[];
}

@Component({
  selector: "app-project-report-dashboard",
  templateUrl: "./dashboard-report.component.html",
  styleUrls: ["./dashboard-report.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardReportComponent implements OnInit, OnDestroy {
  @ViewChild("pdfRoot", { static: false }) pdfRoot!: ElementRef<HTMLElement>;
  @Input() projectIdInput = "";
  @Input() autoDownload = true;
  @Output() reportLoaded = new EventEmitter<{ projectId: string; projectName: string }>();

  private destroy$ = new Subject<void>();
  private autoDownloadStarted = false;
  private autoDownloadTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly pdfPageRenderTimeoutMs = 30000;
  private readonly pdfRenderScale = 2;
  private readonly pdfImageQuality = 0.95;

  isLoading = true;
  isPdfGenerating = false;
  reportUnavailable = false;
  reportErrorMessage = "";
  dataWarning = "";
  autoDownloadMessage = "";

  projectId = "";
  projectName = "Project Report";
  projectTitle = "Project Report";
  projectRouteDetails = "";
  today = new Date();

  overviewData: AnyObj = {};
  toBeCompleted: AnyObj = {};
  completedTillNow: AnyObj = {};
  inPercentage: AnyObj = {};
  chartData: ChartResponse | null = null;
  pnDetails: AnyObj = {};
  newProgressDetails: AnyObj = {};

  principalSecretary = "N/A";
  commissioners: string[] = [];
  districtCollectors: string[] = [];
  calaOfficers: string[] = [];

  kpis: KpiItem[] = [];
  progressRows: ProgressRow[] = [];
  summarySections: SummarySection[] = [];
  districtSections: DistrictReportSection[] = [];

  constructor(
    private route: ActivatedRoute,
    private dashboardService: DashboardService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const projectId$ = this.projectIdInput
      ? of(this.projectIdInput)
      : this.route.paramMap.pipe(
          map((params) => params.get("id") || ""),
          filter((id) => !!id)
        );

    projectId$
      .pipe(
        switchMap((id) => {
          this.projectId = id;
          this.isLoading = true;
          this.dataWarning = "";
          this.autoDownloadMessage = this.autoDownload
            ? "Preparing project report download..."
            : "";
          this.autoDownloadStarted = false;
          this.clearAutoDownloadTimer();
          this.cdr.markForCheck();

          return forkJoin({
            report: this.dashboardService
              .getSingleProjectDetailsReport(id)
              .pipe(catchError((error) => of(this.toReportLoadFailure(error, "report")))),
            chart: this.dashboardService
              .getGraphData(id)
              .pipe(catchError((error) => of(this.toReportLoadFailure(error, "chart")))),
          });
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(({ report, chart }: { report: ProjectReportResponse | ReportLoadFailure; chart: ChartResponse | ReportLoadFailure }) => {
        const failure = this.getReportLoadFailure(report, chart);

        if (failure) {
          this.reportUnavailable = true;
          this.reportErrorMessage = failure.message;
          this.dataWarning = "";
          this.autoDownloadMessage = "";
          this.isPdfGenerating = false;
          this.clearAutoDownloadTimer();
          this.resetReportData();
          this.isLoading = false;
          this.cdr.markForCheck();
          this.notifyAutoDownloadParent("error", failure.message);
          return;
        }

        this.reportUnavailable = false;
        this.reportErrorMessage = "";
        this.chartData = chart as ChartResponse;
        this.bindReport(report as ProjectReportResponse);
        this.dataWarning = "";
        this.isLoading = false;
        this.autoDownloadMessage = this.autoDownload ? "Preparing PDF download..." : "";
        this.reportLoaded.emit({
          projectId: this.projectId,
          projectName: this.projectName,
        });
        this.cdr.markForCheck();
        if (this.autoDownload) {
          this.scheduleAutoDownload();
        }
      });
  }

  private getReportLoadFailure(
    report: ProjectReportResponse | ReportLoadFailure,
    chart: ChartResponse | ReportLoadFailure
  ): ReportLoadFailure | null {
    const reportFailure = this.isReportLoadFailure(report) ? report : this.getDetailFailure(report, "report");
    const chartFailure = this.isReportLoadFailure(chart) ? chart : this.getDetailFailure(chart, "chart");

    if (chartFailure?.hasBackendMessage) return chartFailure;
    if (reportFailure?.hasBackendMessage) return reportFailure;
    if (chartFailure) return chartFailure;
    if (reportFailure) return reportFailure;
    return null;
  }

  private isReportLoadFailure(value: ProjectReportResponse | ChartResponse | ReportLoadFailure | null | undefined): value is ReportLoadFailure {
    return !!value && typeof value === "object" && "source" in value && "message" in value;
  }

  private toReportLoadFailure(error: any, source: "report" | "chart"): ReportLoadFailure {
    const backendMessage = this.extractBackendErrorMessage(error);
    return {
      source,
      message: backendMessage || "Report data could not be loaded for this project.",
      hasBackendMessage: !!backendMessage,
    };
  }

  private getDetailFailure(value: AnyObj | null | undefined, source: "report" | "chart"): ReportLoadFailure | null {
    const detail = value?.detail;
    if (!detail) return null;
    return {
      source,
      message: this.text(detail, "Report data could not be loaded for this project."),
      hasBackendMessage: true,
    };
  }

  private extractBackendErrorMessage(error: any): string {
    const errorBody = error?.error;

    if (errorBody?.detail) return String(errorBody.detail);
    if (errorBody?.message) return String(errorBody.message);
    if (errorBody?.Error) return String(errorBody.Error);

    if (typeof errorBody === "string") {
      try {
        const parsed = JSON.parse(errorBody);
        return this.text(parsed?.detail || parsed?.message || parsed?.Error, "");
      } catch {
        return errorBody.trim();
      }
    }

    return "";
  }

  private resetReportData(): void {
    this.chartData = null;
    this.overviewData = {};
    this.toBeCompleted = {};
    this.completedTillNow = {};
    this.inPercentage = {};
    this.pnDetails = {};
    this.newProgressDetails = {};
    this.principalSecretary = "N/A";
    this.commissioners = [];
    this.districtCollectors = [];
    this.calaOfficers = [];
    this.kpis = [];
    this.progressRows = [];
    this.summarySections = [];
    this.districtSections = [];
  }

  private bindReport(report: ProjectReportResponse): void {
    const districts = Array.isArray(report.districts)
      ? [...report.districts]
      : [];

    this.projectName = this.text(report.name, "Project Report");
    this.setProjectHeading(this.projectName);
    this.overviewData = this.normalizeOverview(report.overview || {});
    this.toBeCompleted = this.normalizeBucket(this.value(this.overviewData, ["to_be_completed", "toBeCompleted"], {}));
    this.completedTillNow = this.normalizeBucket(
      this.value(this.overviewData, ["total_completed_till_now", "completed_till_now", "completedTillNow"], {})
    );
    this.inPercentage = report.percentage || {};
    this.pnDetails = report.PN_details || {};
    this.newProgressDetails = report.new_progress_details || {};

    const officers = report.officers_list || {};
    this.principalSecretary = this.text(this.value(officers, ["principal", "principal_secretary"]), "N/A");
    this.commissioners = this.asList(this.value(officers, ["comissioner", "commissioner"]));
    this.districtCollectors = this.asList(this.value(officers, ["district_collector", "district_collectors"]));
    this.calaOfficers = this.asList(this.value(officers, ["cala", "calas"]));

    const villagesCount =
      report.villages_count != null
        ? this.num(report.villages_count)
        : districts.reduce(
            (total, district) =>
              total +
              (Array.isArray(district?.villages)
                ? district.villages.length
                : 0),
            0
          );
    const districtsCount =
      report.districts_count != null
        ? this.num(report.districts_count)
        : districts.length;

    this.kpis = this.buildKpis(villagesCount, districtsCount);
    this.progressRows = this.buildProgressRows();
    this.summarySections = this.buildSummarySections();
    this.districtSections = this.buildDistrictSections(districts);
  }

  private setProjectHeading(projectName: string): void {
    const normalizedName = (projectName || "Project Report")
      .replace(/\s+/g, " ")
      .trim();
    const routeStart = normalizedName.search(/\bfrom\b/i);

    if (routeStart <= 0) {
      this.projectTitle = normalizedName;
      this.projectRouteDetails = "";
      return;
    }

    this.projectTitle = normalizedName.slice(0, routeStart).trim();
    this.projectRouteDetails = normalizedName.slice(routeStart).trim();
  }

  private buildKpis(
    totalVillages: number,
    totalDistricts: number
  ): KpiItem[] {
    return [
      {
        icon: "fa-map",
        color: "purple",
        value: this.integer(totalDistricts),
        label: "Total Districts",
      },
      { icon: "fa-home", color: "blue", value: this.integer(totalVillages), label: "Total Villages" },
      { icon: "fa-check", color: "green", value: this.integer(this.overviewData.completed), label: "Completed" },
      { icon: "fa-clock-o", color: "blue", value: this.integer(this.overviewData.ontime), label: "On Time" },
      { icon: "fa-exclamation", color: "red", value: this.integer(this.overviewData.delayed), label: "Delayed" },
    ];
  }

  private buildProgressRows(): ProgressRow[] {
    return [
      this.progressRow("fa-users", "3(A)", "Land Acquired Under 3(A)", ["3A", "3a"], ["percentage_3A", "percentage_3a"], "Delayed"),
      this.progressRow("fa-inr", "JMS Requests", "Funds Distributed Under 3(H)", ["3H_money", "jms_request"], ["percentage_3H_money", "percentage_jms_request"], "Delayed"),
      this.progressRow("fa-key", "Possession", "Possession of Land", "possession", "percentage_possession", "Delayed"),
      this.progressRow("fa-calendar", "Mutation", "Mutation of Land", "mutation", "percentage_mutation", "Pending"),
      this.progressRow("fa-university", "Govt. Land", "Govt. Land Possession", "govt_possession", "percentage_govt_possession", "N/A"),
    ];
  }

  private progressRow(
    icon: string,
    stage: string,
    description: string,
    valueKeys: string | string[],
    percentKeys: string | string[],
    status: string
  ): ProgressRow {
    const percentage = this.percent(this.value(this.inPercentage, percentKeys, this.calculatedPercent(valueKeys)));
    return {
      icon,
      stage,
      description,
      expected: this.number(this.value(this.toBeCompleted, valueKeys)),
      achieved: this.number(this.value(this.completedTillNow, valueKeys)),
      percentage,
      percentValue: this.percentNumber(percentage),
      status,
    };
  }

  private buildSummarySections(): SummarySection[] {
    return [
      {
        title: "SCHEDULE",
        className: "blue-block",
        columns: ["Item", "Scheduled Date", "To Be Completed", "Completed Till Now", "In Percentage"],
        rows: [["Scheduled date of completion", this.date(this.value(this.chartData, "date_of_3a_expected"), "N/A", true), "N/A", "N/A", "N/A"]],
      },
      {
        title: "LAND",
        className: "green-block",
        columns: ["Item", "To Be Completed (Ha)", "Completed Till Now (Ha)", "In Percentage"],
        rows: [
          ["Area To Be Acquired", this.totalAreaText, this.acquiredAreaText, this.overallAreaProgressText],
          ["3(A) Land Acquired Under 3(A)", this.number(this.value(this.toBeCompleted, ["3A", "3a"])), this.number(this.value(this.completedTillNow, ["3A", "3a"])), this.percent(this.value(this.inPercentage, ["percentage_3A", "percentage_3a"]))],
          ["3(C)", this.number(this.value(this.toBeCompleted, "3C")), this.number(this.value(this.completedTillNow, "3C")), this.percent(this.value(this.inPercentage, "percentage_3C"))],
          ["3(D)", this.number(this.value(this.toBeCompleted, "3D")), this.number(this.value(this.completedTillNow, "3D")), this.percent(this.value(this.inPercentage, "percentage_3D"))],
          ["3(G)", this.number(this.value(this.toBeCompleted, "3G")), this.number(this.value(this.completedTillNow, "3G")), this.percent(this.value(this.inPercentage, "percentage_3G"))],
          ["Possession", this.number(this.value(this.toBeCompleted, "possession")), this.number(this.value(this.completedTillNow, "possession")), this.percent(this.value(this.inPercentage, "percentage_possession"))],
          ["Mutation", this.number(this.value(this.toBeCompleted, "mutation")), this.number(this.value(this.completedTillNow, "mutation")), this.percent(this.value(this.inPercentage, "percentage_mutation"))],
        ],
      },
      {
        title: "FUNDS",
        className: "orange-block",
        columns: ["Item", "To Be Completed (Rs Lakh)", "Completed Till Now (Rs Lakh)", "In Percentage"],
        rows: [
          ["JMS Requests", this.number(this.value(this.toBeCompleted, "jms_request")), this.number(this.value(this.completedTillNow, "jms_request")), this.percent(this.value(this.inPercentage, "percentage_jms_request"))],
          ["3(H) Funds Distributed Under 3(H)", this.number(this.value(this.toBeCompleted, "3H_money")), this.number(this.value(this.completedTillNow, "3H_money")), this.percent(this.value(this.inPercentage, "percentage_3H_money"))],
        ],
      },
      {
        title: "GOVERNMENT LAND",
        className: "purple-block",
        columns: ["Item", "To Be Completed (Ha)", "Completed Till Now (Ha)", "In Percentage"],
        rows: [
          ["Govt. Land Possession", this.number(this.value(this.toBeCompleted, "govt_possession")), this.number(this.value(this.completedTillNow, "govt_possession")), this.percent(this.value(this.inPercentage, "percentage_govt_possession"), "N/A")],
          ["Govt. Land Mutation", this.number(this.value(this.toBeCompleted, "govt_mutation")), this.number(this.value(this.completedTillNow, "govt_mutation")), this.percent(this.value(this.inPercentage, "percentage_govt_mutation"), "N/A")],
          ["Area to be acquired by PN", this.number(this.value(this.overviewData, "total_private_land_aquired")), this.number(this.value(this.completedTillNow, "private_land_aquired")), this.percent(this.value(this.inPercentage, "percentage_private_land_aquired"), "N/A")],
        ],
      },
      {
        title: "PN (PUBLIC NOTICE / COMPENSATION)",
        className: "teal-block",
        columns: ["Item", "To Be Completed (Rs Lakh)", "Completed Till Now (Rs Lakh)", "In Percentage"],
        rows: [
          ["PN fund deposition", this.number(this.value(this.overviewData, "total_private_fund_deposition")), this.number(this.value(this.completedTillNow, "private_fund_deposition")), this.percent(this.value(this.inPercentage, "percentage_private_fund_deposition"), "N/A")],
          ["PN fund distribution", this.number(this.value(this.overviewData, "total_private_fund_distribution")), this.number(this.value(this.completedTillNow, "private_fund_distribution")), this.percent(this.value(this.inPercentage, "percentage_private_fund_distribution"), "N/A")],
        ],
      },
    ];
  }

  private buildDistrictSections(
    districts: AnyObj[]
  ): DistrictReportSection[] {
    let nextPageNumber = 3;

    return districts.map((district, districtIndex) => {
      const districtName = this.text(
        this.value(district, ["district_name", "name"]),
        `District ${districtIndex + 1}`
      );
      const districtId = this.text(
        this.value(district, ["district_id", "id"]),
        "N/A"
      );
      const districtDataIds = Array.isArray(district?.district_data_ids)
        ? district.district_data_ids.join(", ")
        : this.text(district?.district_data_ids, "N/A");
      const summary = this.normalizeOverview(district?.summary || {});
      const toBeCompleted = this.normalizeBucket(
        this.value(summary, ["to_be_completed", "toBeCompleted"], {})
      );
      const completedTillNow = this.normalizeBucket(
        this.value(
          summary,
          [
            "total_completed_till_now",
            "completed_till_now",
            "completedTillNow",
          ],
          {}
        )
      );
      const percentage = this.value(
        summary,
        "percentage",
        district?.percentage || {}
      );
      const villages = (
        Array.isArray(district?.villages) ? district.villages : []
      ).map((village) => ({
        ...village,
        district_name:
          this.value(village, "district_name", districtName) || districtName,
      }));
      const villagesCount =
        district?.villages_count != null
          ? this.num(district.villages_count)
          : villages.length;
      const totalArea = this.num(
        this.value(summary, "total_land_aquire")
      );
      const acquiredArea = this.num(
        this.value(completedTillNow, "3H_area")
      );
      const overviewPageNumber = nextPageNumber++;
      const villagePages = this.chunk(villages, 3).map(
        (pageVillages, pageIndex): DistrictVillagePage => ({
          villages: pageVillages,
          pageNumber: nextPageNumber++,
          startIndex: pageIndex * 3,
        })
      );

      return {
        districtId,
        districtName,
        districtDataIds,
        villagesCount,
        completed: this.integer(summary.completed),
        onTime: this.integer(summary.ontime),
        delayed: this.integer(summary.delayed),
        totalArea: this.number(totalArea),
        acquiredArea: this.number(acquiredArea),
        remainingArea: this.number(Math.max(totalArea - acquiredArea, 0)),
        progress: totalArea
          ? this.percent((acquiredArea / totalArea) * 100)
          : "0.00%",
        overviewPageNumber,
        summarySections: this.buildDistrictSummarySections(
          districtName,
          districtId,
          districtDataIds,
          villagesCount,
          summary,
          toBeCompleted,
          completedTillNow,
          percentage
        ),
        villagePages,
      };
    });
  }

  private buildDistrictSummarySections(
    districtName: string,
    districtId: string,
    districtDataIds: string,
    villagesCount: number,
    summary: AnyObj,
    toBeCompleted: AnyObj,
    completedTillNow: AnyObj,
    percentage: AnyObj
  ): SummarySection[] {
    const progressRow = (
      label: string,
      valueKeys: string | string[],
      percentageKeys: string | string[],
      useDecimals = true
    ): string[] => {
      const expected = this.value(toBeCompleted, valueKeys);
      const achieved = this.value(completedTillNow, valueKeys);

      return [
        label,
        useDecimals ? this.number(expected) : this.integer(expected),
        useDecimals ? this.number(achieved) : this.integer(achieved),
        this.percent(this.value(percentage, percentageKeys)),
      ];
    };

    return [
      {
        title: "DISTRICT STATUS",
        className: "blue-block",
        columns: ["Item", "Value"],
        rows: [
          ["District", districtName],
          ["District ID", districtId],
          ["District Data ID(s)", districtDataIds],
          ["Total Villages", this.integer(villagesCount)],
          ["Completed", this.integer(summary.completed)],
          ["On Time", this.integer(summary.ontime)],
          ["Delayed", this.integer(summary.delayed)],
        ],
      },
      {
        title: "LAND ACQUISITION PROGRESS",
        className: "green-block",
        columns: [
          "Item",
          "To Be Completed",
          "Completed Till Now",
          "In Percentage",
        ],
        rows: [
          progressRow(
            "Villages",
            "villages",
            ["percentage_villages", "percentage_village"],
            false
          ),
          progressRow("3(a) Requests", "3a", "percentage_3a", false),
          progressRow(
            "JMS Requests",
            "jms_request",
            "percentage_jms_request",
            false
          ),
          progressRow("JMS", "jms", "percentage_jms"),
          progressRow("3(A)", ["3A", "3a"], [
            "percentage_3A",
            "percentage_3a",
          ]),
          progressRow("3(C)", "3C", "percentage_3C"),
          progressRow("3(D)", "3D", "percentage_3D"),
          progressRow("3(G)", "3G", "percentage_3G"),
          progressRow(
            "Land Acquired Under 3(H)",
            "3H_area",
            "percentage_3H_area"
          ),
          progressRow(
            "CALA Fund Area",
            "cala_fund_area",
            "percentage_cala_fund_area"
          ),
          progressRow(
            "Possession",
            "possession",
            "percentage_possession"
          ),
          progressRow("Mutation", "mutation", "percentage_mutation"),
        ],
      },
      {
        title: "FUNDS, GOVERNMENT LAND & PRIVATE NEGOTIATION",
        className: "orange-block",
        columns: [
          "Item",
          "To Be Completed",
          "Completed Till Now",
          "In Percentage",
        ],
        rows: [
          progressRow(
            "Funds Distributed Under 3(H)",
            ["3H_money", "cala_fund_money"],
            ["percentage_3H_money", "percentage_cala_fund_money"]
          ),
          progressRow(
            "Govt. Land Possession",
            "govt_possession",
            "percentage_govt_possession"
          ),
          progressRow(
            "Govt. Land Mutation",
            "govt_mutation",
            "percentage_govt_mutation"
          ),
          [
            "Area to be acquired by PN",
            this.number(summary.total_private_land_aquired),
            this.number(completedTillNow.private_land_aquired),
            this.percent(percentage.percentage_private_land_aquired),
          ],
          [
            "PN fund deposition",
            this.number(summary.total_private_fund_deposition),
            this.number(completedTillNow.private_fund_deposition),
            this.percent(percentage.percentage_private_fund_deposition),
          ],
          [
            "PN fund distribution",
            this.number(summary.total_private_fund_distribution),
            this.number(completedTillNow.private_fund_distribution),
            this.percent(percentage.percentage_private_fund_distribution),
          ],
        ],
      },
    ];
  }

  get totalAreaText(): string {
    return this.number(this.value(this.overviewData, "total_land_aquire"), "0.00");
  }

  get acquiredAreaText(): string {
    return this.number(this.value(this.completedTillNow, "3H_area"), "0.00");
  }

  get remainingAreaText(): string {
    return Math.max(this.num(this.value(this.overviewData, "total_land_aquire")) - this.num(this.value(this.completedTillNow, "3H_area")), 0).toFixed(2);
  }

  get overallAreaProgressText(): string {
    const total = this.num(this.value(this.overviewData, "total_land_aquire"));
    const acquired = this.num(this.value(this.completedTillNow, "3H_area"));
    return total ? `${((acquired / total) * 100).toFixed(2)}%` : "0.00%";
  }

  get projectStartDate(): string {
    return this.date(this.value(this.chartData, "start_date"));
  }

  get reportTotalPages(): number {
    return (
      2 +
      this.districtSections.reduce(
        (total, district) => total + 1 + district.villagePages.length,
        0
      )
    );
  }

  getOfficerLine(items: string[]): string {
    return items.length ? items.join(", ") : "N/A";
  }

  villageMetrics(village: AnyObj): VillageMetric[] {
    const documents = (stage: string) =>
      this.villageDocumentLinks(village, stage);

    return [
      {
        label: "3(A) Request",
        value: this.number(
          this.value(village, ["land_aquire", "land_acquire"])
        ),
        documents: [],
      },
      {
        label: "JMS Request Status",
        value: this.text(this.value(village, "jms_request_status")),
        documents: [],
      },
      {
        label: "JMS Pending",
        value: this.number(this.value(village, "jms")),
        documents: documents("jms"),
      },
      {
        label: "3(A) Pending",
        value: this.number(this.value(village, ["3A", "3a"])),
        documents: documents("3A"),
      },
      {
        label: "3(C) Pending",
        value: this.number(this.value(village, "3C")),
        documents: documents("3C"),
      },
      {
        label: "3(D) Pending",
        value: this.number(this.value(village, "3D")),
        documents: documents("3D"),
      },
      {
        label: "3(G) Pending",
        value: this.number(this.value(village, "3G")),
        documents: documents("3G"),
      },
      {
        label: "3(H) Pending",
        value: this.number(this.value(village, "3H_area")),
        documents: documents("3H"),
      },
      {
        label: "Funds Distribution Under 3(H)",
        value: this.number(this.value(village, "3H_money")),
        documents: documents("cala_fund"),
      },
      {
        label: "Possession of Land",
        value: this.number(this.value(village, "possession")),
        documents: documents("possession"),
      },
      {
        label: "Mutation of Land",
        value: this.number(this.value(village, "mutation")),
        documents: documents("mutation"),
      },
      {
        label: "Govt. land to be acquired",
        value: this.number(this.value(village, "govt_land_aquire")),
        documents: [],
      },
      {
        label: "Govt. Land Possession",
        value: this.number(this.value(village, "govt_possession")),
        documents: documents("govt_possession"),
      },
      {
        label: "Govt. Land Mutation",
        value: this.number(this.value(village, "govt_mutation")),
        documents: documents("govt_mutation"),
      },
      {
        label: "Total Area to be acquired by PN",
        value: this.number(
          this.value(village, "total_private_land_aquired")
        ),
        documents: [],
      },
      {
        label: "Funds Deposition Under PN",
        value: this.number(
          this.value(village, "private_fund_deposition")
        ),
        documents: documents("private_fund_deposition"),
      },
      {
        label: "Funds Distribution Under PN",
        value: this.number(
          this.value(village, "private_fund_distribution")
        ),
        documents: documents("private_fund_distribution"),
      },
      {
        label: "Area Actually acquired by PN",
        value: this.number(this.value(village, "private_land_aquired")),
        documents: documents("private_land_aquired"),
      },
      {
        label: "Total compensation amount under PN",
        value: this.number(
          this.value(village, "total_private_fund_compensation")
        ),
        documents: [],
      },
    ];
  }

  private villageDocumentLinks(
    village: AnyObj,
    stage: string
  ): VillageDocumentLink[] {
    const documentGroups = this.value(village, "documents", {});
    const stageDocuments = Array.isArray(documentGroups?.[stage])
      ? documentGroups[stage]
      : [];
    const links = stageDocuments
      .map((document: AnyObj) => ({
        url: this.safeDocumentUrl(
          this.value(document, ["document", "url", "file_url", "href"], "")
        ),
        fileName: this.text(
          this.value(document, ["file_name", "name"], ""),
          "Document"
        ),
      }))
      .filter((document) => !!document.url);

    return links.map((document, index) => ({
      ...document,
      label:
        links.length === 1 ? "View Document" : `View ${index + 1}`,
    }));
  }

  private safeDocumentUrl(value: any): string {
    const rawUrl = this.text(value, "").trim();
    if (!rawUrl) return "";

    try {
      const url = new URL(rawUrl, window.location.origin);
      if (url.protocol !== "http:" && url.protocol !== "https:") return "";

      if (
        url.protocol === "http:" &&
        url.hostname.toLowerCase().endsWith("reluconsultancy.in")
      ) {
        url.protocol = "https:";
      }

      return url.toString();
    } catch {
      return "";
    }
  }

  splitMetrics(metrics: VillageMetric[], side: "left" | "right"): VillageMetric[] {
    const mid = Math.ceil(metrics.length / 2);
    return side === "left" ? metrics.slice(0, mid) : metrics.slice(mid);
  }

  statusClass(status: string): string {
    return status.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  }

  trackByIndex(index: number): number {
    return index;
  }

  async downloadPdf(): Promise<void> {
    if (this.reportUnavailable || !this.pdfRoot || this.isPdfGenerating) return;

    const root = this.pdfRoot.nativeElement;
    this.isPdfGenerating = true;
    this.autoDownloadMessage = "Generating PDF download...";
    this.cdr.markForCheck();

    try {
      root.classList.add("pdf-mode");
      await this.waitForFonts();
      await this.waitForImages(root);

      const pages = Array.from(root.querySelectorAll(".pdf-page")) as HTMLElement[];
      if (!pages.length) throw new Error("No report pages found.");

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
      const width = pdf.internal.pageSize.getWidth();
      const height = pdf.internal.pageSize.getHeight();

      for (let i = 0; i < pages.length; i++) {
        this.autoDownloadMessage = `Generating PDF page ${i + 1} of ${pages.length}...`;
        this.cdr.detectChanges();
        await this.nextPaint();
        const canvas = await this.renderPdfPage(pages[i]);

        if (i > 0) pdf.addPage();
        pdf.addImage(
          canvas.toDataURL("image/jpeg", this.pdfImageQuality),
          "JPEG",
          0,
          0,
          width,
          height
        );
        this.addPdfDocumentLinks(pdf, pages[i], width, height);
        canvas.width = 0;
        canvas.height = 0;
      }

      this.autoDownloadMessage = "Starting PDF download...";
      this.cdr.detectChanges();
      await this.nextPaint();
      pdf.save(this.getPdfFileName());
      this.autoDownloadMessage = "";
      this.notifyAutoDownloadParent("complete");
    } catch (error) {
      this.autoDownloadMessage = "";
      this.dataWarning = "Unable to auto-download the PDF. Please refresh the report and try again.";
      console.error("Project report PDF generation failed", error);
      this.notifyAutoDownloadParent("error", this.dataWarning);
    } finally {
      root.classList.remove("pdf-mode");
      this.isPdfGenerating = false;
      this.cdr.markForCheck();
    }
  }

  private notifyAutoDownloadParent(
    status: "complete" | "error",
    message = ""
  ): void {
    if (!this.autoDownload || window.parent === window) {
      return;
    }

    window.parent.postMessage(
      { type: "nhai-report-auto-download", status, message },
      window.location.origin
    );
  }

  private renderPdfPage(page: HTMLElement): Promise<HTMLCanvasElement> {
    const renderPromise = html2canvas(page, {
      backgroundColor: "#ffffff",
      scale: this.pdfRenderScale,
      useCORS: true,
      allowTaint: true,
      logging: false,
      imageTimeout: 10000,
      removeContainer: true,
      scrollX: 0,
      scrollY: 0,
      windowWidth: page.scrollWidth,
      windowHeight: page.scrollHeight,
    });

    const timeoutPromise = new Promise<HTMLCanvasElement>((_, reject) => {
      setTimeout(
        () => reject(new Error("Timed out while rendering a PDF page.")),
        this.pdfPageRenderTimeoutMs
      );
    });

    return Promise.race([renderPromise, timeoutPromise]);
  }

  private addPdfDocumentLinks(
    pdf: jsPDF,
    page: HTMLElement,
    pdfWidth: number,
    pdfHeight: number
  ): void {
    const pageRect = page.getBoundingClientRect();
    if (!pageRect.width || !pageRect.height) return;

    const scaleX = pdfWidth / pageRect.width;
    const scaleY = pdfHeight / pageRect.height;
    const links = Array.from(
      page.querySelectorAll<HTMLAnchorElement>("a[data-pdf-link]")
    );

    links.forEach((link) => {
      const url = this.safeDocumentUrl(link.href);
      const linkRect = link.getBoundingClientRect();
      const left = Math.max(linkRect.left, pageRect.left);
      const top = Math.max(linkRect.top, pageRect.top);
      const right = Math.min(linkRect.right, pageRect.right);
      const bottom = Math.min(linkRect.bottom, pageRect.bottom);

      if (!url || right <= left || bottom <= top) return;

      pdf.link(
        (left - pageRect.left) * scaleX,
        (top - pageRect.top) * scaleY,
        (right - left) * scaleX,
        (bottom - top) * scaleY,
        { url }
      );
    });
  }

  private scheduleAutoDownload(): void {
    if (this.autoDownloadStarted || this.reportUnavailable) return;

    this.clearAutoDownloadTimer();
    this.autoDownloadTimer = setTimeout(() => this.tryAutoDownload(0), 3000);
  }

  private tryAutoDownload(attempt: number): void {
    const hasPages = !!this.pdfRoot?.nativeElement?.querySelector(".pdf-page");

    if (!this.isLoading && !this.isPdfGenerating && hasPages) {
      this.autoDownloadStarted = true;
      this.downloadPdf();
      return;
    }

    if (attempt >= 20 || this.autoDownloadStarted) return;

    this.autoDownloadTimer = setTimeout(() => this.tryAutoDownload(attempt + 1), 500);
  }

  private clearAutoDownloadTimer(): void {
    if (!this.autoDownloadTimer) return;
    clearTimeout(this.autoDownloadTimer);
    this.autoDownloadTimer = null;
  }

  private getPdfFileName(): string {
    const name = (this.projectName || "project-report")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80);

    return `${name || "project-report"}-${this.projectId || "preview"}.pdf`;
  }

  private normalizeOverview(source: AnyObj): AnyObj {
    const overview = { ...source };
    this.alias(overview, "total_land_aquire", ["total_land_acquire", "total_land_aquare", "land_aquire", "land_acquire"]);
    this.alias(overview, "total_private_land_aquired", ["total_private_land_acquired", "total_area_aquired_PN", "total_area_aquired_pn"]);
    this.alias(overview, "total_private_fund_deposition", ["fund_deposition", "private_fund_deposition"]);
    this.alias(overview, "total_private_fund_distribution", ["fund_distribution", "private_fund_distribution"]);
    return overview;
  }

  private normalizeBucket(source: AnyObj): AnyObj {
    const bucket = { ...source };
    this.alias(bucket, "3A", ["threeA", "land_acquired_3A"]);
    this.alias(bucket, "3a", ["3A_request", "date_of_3a"]);
    this.alias(bucket, "3C", ["threeC"]);
    this.alias(bucket, "3D", ["threeD"]);
    this.alias(bucket, "3G", ["threeG"]);
    this.alias(bucket, "jms", ["JMS"]);
    this.alias(bucket, "jms_request", ["JMS_request", "jms_requests"]);
    this.alias(bucket, "3H_area", ["threeH_area"]);
    this.alias(bucket, "3H_money", ["threeH_money"]);
    this.alias(bucket, "private_land_aquired", ["private_land_acquired", "actual_area_aquired", "actual_area_aquired_pn"]);
    return bucket;
  }

  private alias(target: AnyObj, canonical: string, aliases: string[]): void {
    if (target[canonical] !== null && target[canonical] !== undefined && target[canonical] !== "") return;
    for (const key of aliases) {
      if (target[key] !== null && target[key] !== undefined && target[key] !== "") {
        target[canonical] = target[key];
        return;
      }
    }
  }

  private value(source: AnyObj | null | undefined, keys: string | string[], fallback: any = null): any {
    if (!source) return fallback;
    const list = Array.isArray(keys) ? keys : [keys];
    for (const key of list) {
      const val = source[key];
      if (val !== null && val !== undefined && val !== "") return val;
    }
    return fallback;
  }

  private asList(value: any): string[] {
    if (Array.isArray(value)) return value.filter((item) => item !== null && item !== undefined && item !== "").map((item) => String(item));
    if (value === null || value === undefined || value === "") return [];
    return String(value).split(",").map((item) => item.trim()).filter((item) => !!item);
  }

  private chunk<T>(items: T[], size: number): T[][] {
    const pages: T[][] = [];
    for (let i = 0; i < items.length; i += size) pages.push(items.slice(i, i + size));
    return pages;
  }

  private calculatedPercent(keys: string | string[]): string {
    const expected = this.num(this.value(this.toBeCompleted, keys));
    const achieved = this.num(this.value(this.completedTillNow, keys));
    return expected ? `${((achieved / expected) * 100).toFixed(2)}%` : "0.00%";
  }

  private text(value: any, fallback = "N/A"): string {
    return value === null || value === undefined || value === "" ? fallback : String(value);
  }

  private integer(value: any): string {
    return String(Math.round(this.num(value)));
  }

  private number(value: any, fallback = "0.00"): string {
    if (value === null || value === undefined || value === "") return fallback;
    const n = Number(value);
    return isFinite(n) ? n.toFixed(2) : fallback;
  }

  private percent(value: any, fallback = "0.00%"): string {
    if (value === null || value === undefined || value === "") return fallback;
    const n = Number(String(value).replace("%", ""));
    return isFinite(n) ? `${n.toFixed(2)}%` : String(value);
  }

  private percentNumber(value: any): number {
    const n = Number(String(value || "0").replace("%", ""));
    return isFinite(n) ? Math.max(0, Math.min(100, n)) : 0;
  }

  private date(value: any, fallback = "N/A", shortYear = false): string {
    if (!value) return fallback;
    const date = new Date(value);
    if (isNaN(date.getTime())) return fallback;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear());
    return `${day}/${month}/${shortYear ? year.slice(-2) : year}`;
  }

  private num(value: any): number {
    const n = Number(value);
    return isFinite(n) ? n : 0;
  }

  private async waitForImages(root: HTMLElement): Promise<void> {
    const images = Array.prototype.slice.call(root.querySelectorAll("img")) as HTMLImageElement[];
    await Promise.all(
      images
        .filter((img) => !img.complete)
        .map((img) => new Promise<void>((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve();
        }))
    );
    await this.nextPaint();
  }

  private async waitForFonts(): Promise<void> {
    const doc = document as Document & { fonts?: { ready: Promise<unknown> } };
    if (doc.fonts?.ready) await doc.fonts.ready;
    await this.nextPaint();
  }

  private async nextPaint(): Promise<void> {
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  }

  ngOnDestroy(): void {
    this.clearAutoDownloadTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
